// src/components/ReportIssueModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { RxCross2 } from 'react-icons/rx';
import { MdMovie, MdVolumeUp, MdSubtitles } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '8px',
    padding: '20px',
    border: 'none',
    background: '#333',
    color: '#fff',
    zIndex: 1000,
    width: 'min(560px, 92vw)'
  }
};

// Default options used as basis; the first option's title/desc may be adjusted
// at runtime depending on whether we are reporting a film or an episode.
const BASE_OPTIONS = [
  { key: 'episode', title: 'Nefunguje epizoda', desc: 'nefunguje epizoda nebo se vám špatně načítá' },
  { key: 'av', title: 'Zvuk a video', desc: 'zvuk a video nefungují jak by měli' },
  { key: 'subs', title: 'Titulky', desc: 'titulky jsou nesprávné nebo nefungují' },
];

const ICON_MAP = {
  episode: MdMovie,
  av: MdVolumeUp,
  subs: MdSubtitles,
};

export default function ReportIssueModal({ isOpen, onClose }) {
  const { user, role } = useAuth();
  // Infer current play meta so we can adjust wording (episode vs film)
  const playMeta = (typeof window !== 'undefined' && window.__play_meta) ? window.__play_meta : null;
  const inferredType = playMeta && (playMeta.contentType || ((playMeta.season != null || playMeta.index != null) ? 'serial' : 'film'));
  const OPTIONS = BASE_OPTIONS.map(opt => ({ ...opt }));
  if (inferredType === 'film') {
    // adjust episode option wording for films
    const idx = OPTIONS.findIndex(o => o.key === 'episode');
    if (idx >= 0) {
      OPTIONS[idx].title = 'Nefunguje film';
      OPTIONS[idx].desc = 'film nefunguje nebo se špatně přehrává';
    }
  }
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [stage, setStage] = useState('options'); // options | confirm | success | limited
  const [selected, setSelected] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null); // { limit, used }

  // Reset state each time the modal is (re)opened so the flow always starts fresh
  useEffect(() => {
    if (isOpen) {
      setStage('options');
      setSelected(null);
      setBusy(false);
      setMessage('');
      setError('');
      setLimitInfo(null);
    }
  }, [isOpen]);

  const submit = async (type) => {
    if (!user) {
      setError('Pro nahlášení se prosím přihlaste.');
      return;
    }
    setBusy(true); setMessage(''); setError('');
    if (!type) { setError('Vyberte důvod nahlášení.'); setBusy(false); return; }
    try {
      const pm = (window.__play_meta || null);
      const inferredType = pm && (pm.contentType || ((pm.season != null || pm.index != null) ? 'serial' : 'film'));
      const payload = {
        userId: user.uid,
        userName: user.displayName || user.email || 'Neznámý',
        role: role || 'User',
        type,
        pagePath: window.location && (window.location.pathname + window.location.search),
        playMeta: pm ? { ...pm, contentType: inferredType } : null,
      };
      const r = await fetch('/api/reports', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) {
        const data = await r.json().catch(()=>({}));
        if (r.status === 429) {
          const limit = data && (data.limit ?? null);
          const used = data && (data.used ?? null);
          setLimitInfo({ limit, used });
          setStage('limited');
        } else {
          setError(data && data.error ? `Chyba: ${data.error}` : 'Nahlášení se nepodařilo odeslat.');
        }
        setBusy(false);
        return;
      }
      setMessage('Děkujeme za nahlášení. Administrátoři se na to podívají.');
      setBusy(false);
      setStage('success');
    } catch (e) {
      setError('Nahlášení se nepodařilo odeslat.');
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={!!isOpen} onRequestClose={onClose} contentLabel="Nahlásit problém" style={modalStyles}>
      <CloseBtn aria-label="Zavřít" onClick={onClose}>
          <RxCross2 size={22} />
        </CloseBtn>
      {stage === 'options' && (
        <>
          <Title>Jaký máte problém?</Title>
          <Options>
            {OPTIONS.map(opt => {
              const Icon = ICON_MAP[opt.key] || MdMovie;
              return (
                <Option key={opt.key} onClick={() => { if (!busy) { setSelected(opt); setStage('confirm'); } }} disabled={busy}>
                  <IconWrap><Icon size={18} /></IconWrap>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <h4>{opt.title}</h4>
                    <p>{opt.desc}</p>
                  </div>
                </Option>
              );
            })}
          </Options>
          {error && <Err>{error}</Err>}
          {/* Close via top-right cross instead of bottom button */}
        </>
      )}
      {stage === 'limited' && (
        <>
          <Title>Denní limit nahlášení vyčerpán</Title>
          <Msg>
            {limitInfo?.limit ? (
              <>Dnes už nemůžete nahlásit další problém. Limit je {limitInfo.limit} za den{typeof limitInfo.used === 'number' ? ` (využito ${limitInfo.used})` : ''}.</>
            ) : (
              <>Dnes už nemůžete nahlásit další problém.</>
            )}
          </Msg>
          <Actions>
            <Btn onClick={onClose}>OK</Btn>
          </Actions>
        </>
      )}
      {stage === 'confirm' && (
        <>
          <Title>Opravdu chcete tohle nahlášení odeslat?</Title>
          <InfoRow>
            <strong>{selected?.title}</strong>
            <small>{selected?.desc}</small>
          </InfoRow>
          {error && <Err>{error}</Err>}
          <CenterActions>
            <Btn onClick={() => { setSelected(null); onClose && onClose(); }} disabled={busy}>Ne</Btn>
            <SubmitBtn onClick={() => submit(selected?.key)} disabled={busy}>{busy ? 'Odesílám…' : 'Ano'}</SubmitBtn>
          </CenterActions>
        </>
      )}
      {stage === 'success' && (
        <>
          <Title>Děkujeme za nahlášení</Title>
          <Msg>Administrátoři se na toto podívají.</Msg>
          <Actions>
            <Btn onClick={onClose}>OK</Btn>
          </Actions>
        </>
      )}
    </Modal>
  );
}

const Title = styled.h3`
  margin: 0 0 12px 0;
`;
const Options = styled.div`
  display: grid;
  gap: 10px;
`;
const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  background: #3b3b3b;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 12px 14px;
  color: #fff;
  cursor: pointer;
  transition: background .18s ease, transform .12s ease, box-shadow .12s ease;
  &:hover { background: #444; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.35); }
  &:active { transform: translateY(-1px); }
  h4 { margin: 0 0 6px 0; }
  p { margin: 0; opacity: .92; font-size: 14px; }
`;

const IconWrap = styled.div`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #cde;
  flex-shrink: 0;
`;
const Actions = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
`;
const CenterActions = styled(Actions)`
  justify-content: center;
  gap: 14px; /* trošilinku větší rozestup */
`;
const Btn = styled.button`
  background: #555;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  &:hover { background: #666; }
`;
const SubmitBtn = styled(Btn)`
  background: #22c55e;
  &:hover { background: #16a34a; }
`;
const Msg = styled.div`
  color: #9BE169;
  margin-top: 10px;
`;
const Err = styled.div`
  color: #ff6b6b;
  margin-top: 10px;
`;
const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  strong { font-size: 16px; }
  small { opacity: .9; }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px; /* slightly lower */
  right: 8px; /* slightly more to the left */
  /* make the button only as big as the icon so only the icon is clickable */
  padding: 0;
  width: auto;
  height: auto;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,0.86);
  transition: color .12s ease;
  will-change: color;
  /* keep background transparent always so only the icon "lights up" */
  &:hover { color: #fff; }
  &:active { color: #fff; }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(255,255,255,0.04); }
`;
